// Live conditions for the site, from Open-Meteo. Picked because it needs no API
// key: the viewer is a static bundle served straight off a CDN, so any keyed
// provider would mean either shipping the key to the browser or standing up a
// proxy for one number in the header.
const RIYADH = { latitude: 24.7136, longitude: 46.6753 } as const

export const WEATHER_PLACE = 'Riyadh, Saudi Arabia'

const ENDPOINT =
  'https://api.open-meteo.com/v1/forecast' +
  `?latitude=${RIYADH.latitude}&longitude=${RIYADH.longitude}` +
  '&current=temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,weather_code,is_day' +
  '&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset' +
  '&timezone=Asia/Riyadh&forecast_days=4'

export interface WeatherDay {
  /** ISO date, in Riyadh's timezone. */
  date: string
  code: number
  maxC: number
  minC: number
}

export interface Weather {
  tempC: number
  feelsLikeC: number
  /** Percent. */
  humidity: number
  windKph: number
  code: number
  isDay: boolean
  /** Local clock times, "HH:MM". */
  sunrise: string
  sunset: string
  /** Today first, then the next three. */
  days: WeatherDay[]
}

interface ApiResponse {
  current: {
    temperature_2m: number
    apparent_temperature: number
    relative_humidity_2m: number
    wind_speed_10m: number
    weather_code: number
    is_day: number
  }
  daily: {
    time: string[]
    weather_code: number[]
    temperature_2m_max: number[]
    temperature_2m_min: number[]
    sunrise: string[]
    sunset: string[]
  }
}

/** "2026-08-19T05:31" -> "05:31". */
function clock(iso: string): string {
  return iso.slice(11, 16)
}

export async function fetchWeather(signal?: AbortSignal): Promise<Weather> {
  const response = await fetch(ENDPOINT, { signal })
  if (!response.ok) throw new Error(`weather ${response.status}`)
  const data = (await response.json()) as ApiResponse

  return {
    tempC: data.current.temperature_2m,
    feelsLikeC: data.current.apparent_temperature,
    humidity: data.current.relative_humidity_2m,
    windKph: data.current.wind_speed_10m,
    code: data.current.weather_code,
    isDay: data.current.is_day === 1,
    sunrise: clock(data.daily.sunrise[0]),
    sunset: clock(data.daily.sunset[0]),
    days: data.daily.time.map((date, i) => ({
      date,
      code: data.daily.weather_code[i],
      maxC: data.daily.temperature_2m_max[i],
      minC: data.daily.temperature_2m_min[i],
    })),
  }
}

// WMO codes, collapsed to the bands that read differently on screen. The full
// table separates, say, light from moderate drizzle; a one-line label in a
// header pill does not.
export function weatherLabel(code: number): string {
  if (code === 0) return 'Clear'
  if (code === 1) return 'Mostly clear'
  if (code === 2) return 'Partly cloudy'
  if (code === 3) return 'Overcast'
  if (code === 45 || code === 48) return 'Fog'
  if (code >= 51 && code <= 57) return 'Drizzle'
  if (code >= 61 && code <= 67) return 'Rain'
  if (code >= 71 && code <= 77) return 'Snow'
  if (code >= 80 && code <= 82) return 'Showers'
  if (code === 85 || code === 86) return 'Snow showers'
  if (code >= 95) return 'Thunderstorm'
  return 'Unsettled'
}
